import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// La cifra en la unidad que se lee de un vistazo.
//
// Todo iba en miles, y ochenta y tres millones salían como «$83676K»: hay que
// contar los dígitos para saber de cuánto se habla, y en una tarjeta que se
// mira de reojo eso es no decir nada. De un millón para arriba, en millones.
const cifra = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000000) return '$' + (v / 1000000).toFixed(1) + 'M';
  if (Math.abs(v) >= 1000) return '$' + Math.round(v / 1000) + 'K';
  return '$' + Math.round(v).toLocaleString('es-CO');
};

// El renglón que dice QUÉ es la cifra de arriba.
const PIE = { display: 'block', marginTop: 4, color: '#6b7280', fontSize: 12 };

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
               'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// «may 2025 a oct 2026» — el periodo que suma la tarjeta, con sus fechas.
//
// «¿Por qué el anual da $83,7M si mi cartera rinde $5.256.000 al mes, o sea
// $63.072.000 al año?» La cuenta estaba bien y la cifra también: lo que pasa es
// que «Anual» no enseña UN año, enseña TODOS —dieciocho meses en esa cartera—
// y la pantalla no lo decía por ninguna parte.
//
// Un rótulo que no dice el periodo obliga a adivinarlo, y quien adivina mal
// concluye que el programa está malo. Con las dos fechas, la diferencia entre
// las dos pestañas se explica sola.
const periodo = (desde, hasta) => {
  const mes = (s) => {
    const m = /^(\d{4})-(\d{2})$/.exec(String(s || ''));
    return m ? `${MESES[Number(m[2]) - 1]} ${m[1]}` : null;
  };
  const a = mes(desde);
  const b = mes(hasta);
  if (!a || !b) return null;
  // Un solo mes no se dice dos veces.
  return a === b ? a : `${a} a ${b}`;
};

function Reports() {
  const [reportType, setReportType] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReport('monthly');
  }, []);

  const loadReport = async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/reports/${type}`);
      setData(res.data);
      setReportType(type);
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };

  const COLORS = ['#667eea', '#764ba2', '#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

  return (
    <div className="reports-page">
      <h2>📊 Reportes Financieros</h2>

      <div className="report-buttons">
        <button
          className={`report-btn ${reportType === 'monthly' ? 'active' : ''}`}
          onClick={() => loadReport('monthly')}
        >
          📅 Mensual
        </button>
        <button
          className={`report-btn ${reportType === 'yearly' ? 'active' : ''}`}
          onClick={() => loadReport('yearly')}
        >
          📈 Anual
        </button>
        <button
          className={`report-btn ${reportType === 'category' ? 'active' : ''}`}
          onClick={() => loadReport('category')}
        >
          🏷️ Por Categoría
        </button>
        <button
          className={`report-btn ${reportType === 'client' ? 'active' : ''}`}
          onClick={() => loadReport('client')}
        >
          👥 Por Cliente
        </button>
      </div>

      {loading ? (
        <p>Cargando reporte...</p>
      ) : data ? (
        <>
          {/* Resumen */}
          {data.summary && (
            <div className="cards-container">
              <div className="card">
                <h3>💰 Total Prestado</h3>
                <p className="amount">{cifra(data.summary.total_loaned)}</p>
                <small style={PIE}>capital de los préstamos abiertos</small>
              </div>
              <div className="card earning-card">
                <h3>💵 Total Ganado</h3>
                <p className="amount earning">{cifra(data.summary.total_earned)}</p>
                {/* QUÉ ES, dicho en la tarjeta. Antes esta cifra era lo que la
                    cartera rinde en un mes, no lo cobrado, y las tres tarjetas
                    enseñaban variaciones del mismo número sin que nada dijera
                    cuál era cuál. */}
                {/* DE QUÉ PERIODO HABLA. La cifra es la suma del gráfico de
                    abajo, y sin decirlo las dos pestañas parecían dar lo mismo
                    cuando lo que pasaba es que cada una contaba otra cosa. */}
                <small style={PIE}>
                  {periodo(data.summary.desde, data.summary.hasta)
                    ? `interés cobrado · ${periodo(data.summary.desde, data.summary.hasta)}`
                    : 'interés que ya se ha cobrado'}
                </small>
              </div>
              <div className="card highlight">
                <h3>📊 Proyección</h3>
                <p className="amount earning">{cifra(data.summary.projection)}</p>
                <small style={PIE}>lo que rinde la cartera en un mes</small>
              </div>
            </div>
          )}

          {/* Gráficos */}
          {data.chartData && data.chartData.length > 0 && (
            <div className="charts-container">
              {reportType === 'monthly' || reportType === 'yearly' ? (
                <div className="chart">
                  <h3>Evolución de Ganancias</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                      <Legend />
                      <Line type="monotone" dataKey="ganancia" stroke="#10b981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : reportType === 'category' ? (
                <div className="chart">
                  <h3>Gastos por Categoría</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={data.chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: $${(value / 1000).toFixed(0)}K`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {data.chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="chart">
                  <h3>Clientes con Mayor Deuda</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                      <Bar dataKey="deuda" fill="#667eea" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Tabla de Detalles */}
          {data.details && data.details.length > 0 && (
            <div className="table-container">
              <h3>Detalles</h3>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>Variación</th>
                  </tr>
                </thead>
                <tbody>
                  {data.details.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.name}</td>
                      <td><strong>${(row.value / 1000).toFixed(0)}K</strong></td>
                      <td style={{ color: row.change > 0 ? '#10b981' : '#ef4444' }}>
                        {row.change > 0 ? '↑' : '↓'} {Math.abs(row.change)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <p>Sin datos disponibles</p>
      )}
    </div>
  );
}

export default Reports;
